import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import React from 'react';
import { QuotationWithDetails } from '@/types/quotation';

/**
 * Retorna verdadeiro se a string parece um UUID v4.
 * Usado para detectar FKs não-resolvidas em campos como payment_conditions.
 */
export const isUUID = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

/**
 * Converte uma URL de imagem em base64 para uso seguro em @react-pdf/renderer.
 * Necessário quando o bucket do Supabase usa signed URLs ou políticas restritivas.
 * Fallback silencioso: retorna a URL original se o fetch falhar.
 */
export async function toBase64OrUrl(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    const blob = await response.blob();
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(url);
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

export interface SignedContractData {
  contractHtml: string;
  signatureDataUrl: string;
  contractId: string;
  customerName: string;
  signedAt: Date;
}

export const generateSignedContractPDF = async (data: SignedContractData): Promise<Blob> => {
  // Create a temporary container with the contract HTML
  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = data.contractHtml;
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.top = '0';
  tempContainer.style.width = '794px'; // A4 width in pixels at 96 DPI
  tempContainer.style.backgroundColor = 'white';
  tempContainer.style.padding = '40px';
  tempContainer.style.fontFamily = 'Arial, sans-serif';
  tempContainer.style.fontSize = '14px';
  tempContainer.style.lineHeight = '1.6';
  
  document.body.appendChild(tempContainer);

  try {
    // Add signature section to the contract
    const signatureSection = document.createElement('div');
    signatureSection.style.marginTop = '40px';
    signatureSection.style.borderTop = '1px solid #ccc';
    signatureSection.style.paddingTop = '20px';
    
    signatureSection.innerHTML = `
      <h3 style="margin-bottom: 20px; color: #333;">Assinatura Digital</h3>
      <div style="margin-bottom: 15px;">
        <strong>Assinado por:</strong> ${data.customerName}
      </div>
      <div style="margin-bottom: 15px;">
        <strong>Data da assinatura:</strong> ${data.signedAt.toLocaleDateString('pt-BR')} às ${data.signedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style="margin-bottom: 10px;">
        <strong>Assinatura:</strong>
      </div>
      <div style="border: 1px solid #ccc; padding: 10px; display: inline-block; background: white;">
        <img src="${data.signatureDataUrl}" style="max-width: 200px; height: auto;" alt="Assinatura Digital" />
      </div>
    `;
    
    tempContainer.appendChild(signatureSection);

    // Convert HTML to canvas with optimized settings for contracts
    const canvas = await html2canvas(tempContainer, {
      scale: 1.5, // Optimized scale for multi-page contracts
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      windowWidth: 794,
      scrollX: 0,
      scrollY: 0,
      height: tempContainer.scrollHeight,
      logging: false
    });

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Add metadata
    pdf.setProperties({
      title: `Contrato Assinado - ${data.contractId}`,
      subject: 'Contrato de Locação Assinado Digitalmente',
      author: data.customerName,
      creator: 'Sistema de Contratos',
      keywords: 'contrato, assinatura digital, locação'
    });

    return pdf.output('blob');
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
  }
};

export interface ReceiptPDFData {
  id: string;
  receiptNumber: string;
  contractNumber: string;
  companyData: {
    name: string;
    razao_social?: string | null;
    cnpj?: string | null;
    endereco?: string | null;
    numero?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    cep?: string | null;
    telefone?: string | null;
    email_corporativo?: string | null;
    chave_pix?: string | null;
    logo_url?: string | null;
  };
  customerData: {
    name: string;
    document_type: string;
    document_number: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
  };
  equipments: Array<{
    type: string;
    qty: number;
    unitValue: number;
    totalValue: number;
  }>;
  monthlyValue: number;
  totalValue: number;
  rentalPeriod: string;
  periodDays: number;
  referenceMonth: string;
  periodStart: string;
  periodEnd: string;
  /** L-LOC.F1: vencimento da COBRANÇA (accounts_receivable.due_date). Sem título → sem linha no PDF. */
  dueDate?: string | null;
  notes?: string;
}

/**
 * Gera PDF do recibo de locação via @react-pdf/renderer (Grupo A).
 * Caminho principal para todos os recibos de locação com dados estruturados.
 */
export const generateReceiptPDF = async (data: ReceiptPDFData): Promise<Blob> => {
  const logoBase64 = data.companyData.logo_url
    ? await toBase64OrUrl(data.companyData.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { ReceiptDocument } = await import(
    '@/components/Documents/Templates/ReceiptDocument'
  );
  return pdf(
    React.createElement(ReceiptDocument, {
      ...data,
      companyData: { ...data.companyData, logo_url: logoBase64 },
    })
  ).toBlob();
};


export const uploadSignedContract = async (pdfBlob: Blob, contractId: string): Promise<string> => {
  const fileName = `${contractId}-signed-${Date.now()}.pdf`;
  
  // This would be called from the edge function with proper Supabase client
  // Return the filename for use in the edge function
  return fileName;
};

// ============= QUOTATION PDF — @react-pdf/renderer =============

export interface QuotationPDFDynamicData {
  quotation: QuotationWithDetails;
  company: {
    name: string;
    razao_social?: string;
    cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    telefone?: string;
    email_corporativo?: string;
    logo_url?: string;
  };
  salespersonName?: string;
}

export const generateQuotationPDFDynamic = async (data: QuotationPDFDynamicData): Promise<Blob> => {
  const logoBase64 = data.company.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { QuotationDocument } = await import(
    '@/components/Documents/Templates/QuotationDocument'
  );
  return pdf(
    React.createElement(QuotationDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

// ============= SALES PDF GENERATION — @react-pdf/renderer =============

export interface SalePDFData {
  sale: {
    id: string;
    sale_number: string;
    sale_date: string;
    delivery_date?: string;
    subtotal: number;
    discount_value: number;
    discount_percentage?: number;
    discount_type?: 'percent' | 'fixed';
    global_discount_type?: 'percent' | 'fixed';
    freight_value: number;
    total_value: number;
    notes?: string;
    payment_conditions?: string;
    signature_data?: string;
    signature_customer_name?: string;
    signature_customer_document?: string;
    signature_date?: string;
    customer: {
      name: string;
      document_number?: string;
      email?: string;
      phone?: string;
      address?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zip_code?: string;
    };
    items: Array<{
      quantity: number;
      unit_price: number;
      discount_percentage?: number;
      discount_value?: number;
      discount_type?: 'percentage' | 'fixed' | null;
      total: number;
      notes?: string;
      product: {
        name: string;
        sku?: string;
      } | null;
    }>;
    installments?: Array<{
      installment_number: number;
      due_date: string;
      amount: number;
      payment_method?: { name: string } | null;
    }>;
  };
  company: {
    name: string;
    razao_social?: string;
    cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    telefone?: string;
    email_corporativo?: string;
    logo_url?: string;
  };
  salespersonName?: string;
  /** @deprecated assinatura sempre incluída — ignorado */
  includeSignature?: boolean;
}

export const generateSalePDF = async (data: SalePDFData): Promise<Blob> => {
  const logoBase64 = data.company.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { SaleDocument } = await import('@/components/Documents/Templates/SaleDocument');
  return pdf(
    React.createElement(SaleDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

// =============================================================================
// RETURN RECEIPT PDF — @react-pdf/renderer  (substitui returnReceiptPdfGenerator.ts)
// =============================================================================

import type { ReturnReceiptDocumentProps } from '@/components/Documents/Templates/ReturnReceiptDocument';
export type { ReturnReceiptDocumentProps };

export const generateReturnReceiptPDF = async (
  data: ReturnReceiptDocumentProps
): Promise<Blob> => {
  const logoBase64 = data.company.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;
  const { pdf } = await import('@react-pdf/renderer');
  const { ReturnReceiptDocument } = await import(
    '@/components/Documents/Templates/ReturnReceiptDocument'
  );
  return pdf(
    React.createElement(ReturnReceiptDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

// =============================================================================
// LOAN DOCUMENT PDF — @react-pdf/renderer  (substitui generateLoanPDF)
// =============================================================================

import type { LoanDocumentProps } from '@/components/Documents/Templates/LoanDocument';
export type { LoanDocumentProps };

export const generateLoanDocumentPDF = async (
  data: LoanDocumentProps
): Promise<Blob> => {
  const logoBase64 = data.company?.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;
  const { pdf } = await import('@react-pdf/renderer');
  const { LoanDocument } = await import(
    '@/components/Documents/Templates/LoanDocument'
  );
  return pdf(
    React.createElement(LoanDocument, {
      ...data,
      company: data.company
        ? { ...data.company, logo_url: logoBase64 }
        : undefined,
    })
  ).toBlob();
};

// =============================================================================
// PAYMENT RECEIPT PDF — @react-pdf/renderer  (substitui generateReceiptPDF fallback)
// =============================================================================

import type { PaymentReceiptDocumentProps } from '@/components/Documents/Templates/PaymentReceiptDocument';
export type { PaymentReceiptDocumentProps };

export const generatePaymentReceiptPDF = async (
  data: PaymentReceiptDocumentProps
): Promise<Blob> => {
  const logoBase64 = data.companyData.logo_url
    ? await toBase64OrUrl(data.companyData.logo_url)
    : undefined;
  const { pdf } = await import('@react-pdf/renderer');
  const { PaymentReceiptDocument } = await import(
    '@/components/Documents/Templates/PaymentReceiptDocument'
  );
  return pdf(
    React.createElement(PaymentReceiptDocument, {
      ...data,
      companyData: { ...data.companyData, logo_url: logoBase64 },
    })
  ).toBlob();
};

// =============================================================================
// CONTRACT PDF — @react-pdf/renderer  (modelo customizado por empresa)
// =============================================================================

import type { ContractDocumentProps } from '@/components/Documents/Templates/ContractDocument';
export type { ContractDocumentProps };

export async function generateContractPDF(
  data: ContractDocumentProps
): Promise<Blob> {
  const logoBase64 = data.logoUrl
    ? await toBase64OrUrl(data.logoUrl)
    : undefined;
  const { pdf } = await import('@react-pdf/renderer');
  const { ContractDocument } = await import(
    '@/components/Documents/Templates/ContractDocument'
  );
  return pdf(
    React.createElement(ContractDocument, {
      ...data,
      logoUrl: logoBase64,
    })
  ).toBlob();
}

// =============================================================================
// STOCK TRANSFER PDF — @react-pdf/renderer
// =============================================================================

import QRCode from 'qrcode';
import type { StockTransferDocData } from '@/components/Documents/Templates/StockTransferDocument';

export type { StockTransferDocData };

export const generateStockTransferPDF = async (data: StockTransferDocData): Promise<Blob> => {
  const logoBase64 = data.company?.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { StockTransferDocument } = await import(
    '@/components/Documents/Templates/StockTransferDocument'
  );
  return pdf(
    React.createElement(StockTransferDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

/**
 * Gera data URL do QR Code para uma URL autenticada de recebimento.
 * Alerta C: a URL aponta para rota /dashboard/... que exige login.
 */
export const generateTransferQRCode = async (transferId: string): Promise<string> => {
  const baseUrl = window.location.origin;
  const url = `${baseUrl}/dashboard/stock-transfers/${transferId}/receive`;
  return QRCode.toDataURL(url, { width: 200, margin: 1 });
};

// =============================================================================
// STOCK CHECKLIST PDF — @react-pdf/renderer (Grupo A)
// =============================================================================

import type { StockChecklistDocumentProps } from '@/components/Documents/Templates/StockChecklistDocument';
export type { StockChecklistDocumentProps };

export const generateStockChecklistPDF = async (
  data: StockChecklistDocumentProps
): Promise<Blob> => {
  const logoBase64 = data.company.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { StockChecklistDocument } = await import(
    '@/components/Documents/Templates/StockChecklistDocument'
  );
  return pdf(
    React.createElement(StockChecklistDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

// ============= SERVICE ORDER QUOTATION PDF — @react-pdf/renderer =============

export interface SOQuoteItemForPDF {
  quantity: number;
  unit_price: number;
  total: number;
  notes?: string | null;
  product?: { name: string; sku?: string | null } | null;
  service?: { service_name: string } | null;
}

export interface SOEquipmentGroupForPDF {
  equipment_id: string | null;
  equipment_label: string;
  items: SOQuoteItemForPDF[];
  subtotal: number;
}

export interface ServiceOrderQuotePDFData {
  quotation: {
    quotation_number: string;
    quotation_date: string;
    valid_until: string;
    freight_value: number;
    total_value: number;
    notes?: string | null;
    payment_conditions?: string | null;
    delivery_conditions?: string | null;
    delivery_deadline?: string | null;
    customer: {
      name: string;
      document_number?: string | null;
      email?: string | null;
      phone?: string | null;
      address?: string | null;
      address_number?: string | null;
      neighborhood?: string | null;
      city?: string | null;
      state?: string | null;
    };
  };
  company: {
    name: string;
    razao_social?: string;
    cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    telefone?: string;
    email_corporativo?: string;
    logo_url?: string;
  };
  salespersonName?: string;
  groups: SOEquipmentGroupForPDF[];
}

export const generateServiceOrderQuotePDF = async (
  data: ServiceOrderQuotePDFData
): Promise<Blob> => {
  const logoBase64 = data.company.logo_url
    ? await toBase64OrUrl(data.company.logo_url)
    : undefined;

  const { pdf } = await import('@react-pdf/renderer');
  const { ServiceOrderQuotationDocument } = await import(
    '@/components/Documents/Templates/ServiceOrderQuotationDocument'
  );
  return pdf(
    React.createElement(ServiceOrderQuotationDocument, {
      ...data,
      company: { ...data.company, logo_url: logoBase64 },
    })
  ).toBlob();
};

export default {
  generateQuotationPDFDynamic,
  generateSignedContractPDF,
  generateReceiptPDF,
  generateSalePDF,
  generateReturnReceiptPDF,
  generateLoanDocumentPDF,
  generatePaymentReceiptPDF,
  generateContractPDF,
  generateStockTransferPDF,
  generateTransferQRCode,
  generateStockChecklistPDF,
  generateServiceOrderQuotePDF,
};